import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PiArrowUpRightLight,
  PiCameraLight,
  PiCodeLight,
  PiFilmSlateLight,
  PiGraduationCap,
  PiImage,
  PiLockSimple,
  PiPenNibLight,
  PiSealCheck,
  PiSquaresFourLight,
  PiTag,
  PiVideoCameraLight,
} from 'react-icons/pi';
import SiteSearch from '../../components/Search/SiteSearch';
import VerifiedSchoolChip from '../../components/UI/VerifiedSchoolChip';
import { toPlainText } from '../../components/UI/RichText';
import { universityBadge } from '../Social/universityTheme';
import { serviceRateLabel } from './HomeFeaturedServices';
import { formatUSD } from '../../utils/money';

// The phone home page, "Search desk" (2026-10-19, A of three mockups, after
// Upwork and Fiverr). Search is the first screen: one box that finds anything
// (a Talent / Jobs / Courses switch was tried and dropped). Then a trust
// row, categories, services, talent, jobs, one course, and the closing CTA.
// Home.jsx renders this instead of the desktop page on touch screens under
// 1100px and passes in the data it already loads; the course list is fetched
// here because HomeAcademyCrossSell isn't mounted on phones.

const H2 = 'font-headline text-[22px] font-extrabold leading-tight tracking-[-0.02em]';
const SEE_ALL = 'flex min-h-[44px] flex-none items-end text-[14px] font-semibold underline decoration-press-ink/30 underline-offset-4';
const RAIL = '-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-px-4 px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';
const CHIP = 'flex h-11 flex-none items-center rounded-full border border-press-paper/20 bg-press-paper/[0.06] px-3.5 text-[14px] font-semibold text-press-paper/90';

// Same five as the desktop hero chips (each checked to land on live services).
// Each tile wears its own deep colour with a lighter icon, Fiverr-style
// (2026-10-19); "All categories" stays paper-2 so it reads as the way out.
const CATEGORIES = [
  { label: 'Logo design', icon: PiPenNibLight, bg: '#3B2E16', ic: '#D8B76B' },
  { label: 'Video editing', icon: PiVideoCameraLight, bg: '#1F2A3C', ic: '#9DB2D3' },
  { label: 'Photography', icon: PiCameraLight, bg: '#5A2414', ic: '#F2A684' },
  { label: 'Web development', icon: PiCodeLight, bg: '#1E3D33', ic: '#93C9AD' },
  { label: 'Motion graphics', icon: PiFilmSlateLight, bg: '#2B2340', ic: '#BEADF2' },
];

const TRUST = [
  { icon: PiSealCheck, text: 'Verified by their university' },
  { icon: PiLockSimple, text: 'Payment held in escrow' },
  { icon: PiTag, text: 'Listing a service is free' },
];

function Head({ title, to, label }) {
  return (
    <div className="mb-3.5 flex items-end justify-between gap-4">
      <h2 className={H2}>{title}</h2>
      {to && <Link to={to} className={SEE_ALL}>{label}</Link>}
    </div>
  );
}

function initials(f) {
  return `${f.firstName?.[0] || ''}${f.lastName?.[0] || ''}`.toUpperCase();
}

export default function HomeMobile({ user, chips, services, servicesLoading, jobs, jobsLoading, people, peopleLoading, formatBudget, timeAgo }) {
  const [course, setCourse] = useState(null);

  useEffect(() => {
    fetch('/api/academy/courses')
      .then((r) => r.json())
      .then((d) => setCourse(Array.isArray(d) && d.length ? d[0] : null))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-press-paper font-academy text-press-ink">
      {/* ── Search: the whole first screen, on ink like the desktop hero ── */}
      <section className="relative bg-press-ink px-4 pt-6 text-press-paper">
        {/* The glow gets its own clipped layer: clipping the section would cut off the search results panel. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -right-[120px] -top-[140px] h-[360px] w-[360px] rounded-full bg-[radial-gradient(closest-side,rgba(199,162,82,0.22),transparent)]" />
        </div>
        <h1 className="relative text-balance font-headline text-[31px] font-extrabold leading-[1.1] tracking-[-0.03em]">Hire verified students and alumni.</h1>

        {/* One box for everything (services, people, jobs, courses, Help),
            the same search as the topbar, which hides itself on the home page. */}
        <div className="relative z-10 mt-[18px] text-press-ink">
          <SiteSearch user={user} />
        </div>

        <div className="relative -mx-4 mt-3 flex gap-2 overflow-x-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {chips.map((c) => (
            <Link key={c} to={`/hire-talent?search=${encodeURIComponent(c)}`} className={CHIP}>{c}</Link>
          ))}
        </div>

        {/* Three facts, not three cards */}
        <ul className="relative -mx-4 mt-[22px] grid grid-cols-3 gap-2 border-t border-press-paper/[0.12] px-5 pb-[22px] pt-[18px]">
          {TRUST.map(({ icon: Icon, text }) => (
            <li key={text} className="flex flex-col items-center gap-1.5 text-center text-[13px] font-semibold leading-tight">
              <Icon size={20} className="text-press-brass-light" aria-hidden="true" />
              {text}
            </li>
          ))}
        </ul>
      </section>

      {/* ── Categories, Fiverr-style ── */}
      <section className="px-4 pt-9" aria-label="Browse by category">
        <Head title="Browse by category" />
        <div className="grid grid-cols-3 gap-2">
          {[...CATEGORIES.map((c) => ({ ...c, to: `/hire-talent?search=${encodeURIComponent(c.label)}` })), { label: 'All categories', icon: PiSquaresFourLight, to: '/hire-talent' }].map(({ label, icon: Icon, to, bg, ic }) => (
            <Link
              key={label}
              to={to}
              style={bg ? { background: bg } : undefined}
              className={`flex min-h-[96px] flex-col justify-between gap-4 rounded-[16px] p-3 text-[14px] font-semibold leading-tight transition duration-300 ease-spring active:scale-[0.97] motion-reduce:transition-none ${
                bg ? 'text-white' : 'bg-press-paper-2'
              }`}
            >
              <Icon size={26} style={ic ? { color: ic } : undefined} aria-hidden="true" />
              {label}
            </Link>
          ))}
        </div>
      </section>

      {/* ── Services ── */}
      {(servicesLoading || services.length > 0) && (
        <section className="px-4 pt-9" aria-label="Popular services">
          <Head title="Popular services" to="/hire-talent" label="See all" />
          <div className={RAIL}>
            {servicesLoading
              ? Array.from({ length: 2 }).map((_, i) => <div key={i} className="h-[248px] w-[236px] flex-none rounded-[16px] bg-press-paper-2" />)
              : services.map((l) => {
                  const cover = l.photos?.[0];
                  const owner = l.ownerName || `${l.owner?.firstName || ''} ${l.owner?.lastName || ''}`.trim();
                  const rate = serviceRateLabel(l);
                  return (
                    <Link key={l._id} to={`/hire-talent/listing/${l._id}`} className="w-[236px] flex-none snap-start overflow-hidden rounded-[16px] border border-press-border bg-press-card">
                      <span className="relative flex aspect-[16/10] items-center justify-center bg-press-paper-2 text-press-muted">
                        {cover
                          ? <img src={cover} alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
                          : <PiImage size={26} aria-hidden="true" />}
                      </span>
                      <span className="block px-3.5 pb-3.5 pt-3">
                        {owner && <span className="block truncate text-[13px] text-press-muted">{owner}</span>}
                        <span className="mt-0.5 line-clamp-2 block min-h-[2.7em] text-[15px] font-semibold leading-snug">{l.title}</span>
                        <span className="mt-2.5 flex items-center justify-between gap-2 text-[14px]">
                          <span className="truncate text-press-muted">
                            {l.reviewCount > 0 ? <><span className="text-press-brass-dark" aria-hidden="true">★</span> {l.rating} ({l.reviewCount})</> : l.category}
                          </span>
                          {rate && <span className="flex-none font-bold tabular-nums">From {rate}</span>}
                        </span>
                      </span>
                    </Link>
                  );
                })}
          </div>
        </section>
      )}

      {/* ── Talent, Upwork-style list ── */}
      {(peopleLoading || people.length > 0) && (
        <section className="px-4 pt-9" aria-label="Top talent">
          <Head title="Top talent" to="/browse-freelancers" label="All talent" />
          {peopleLoading
            ? Array.from({ length: 3 }).map((_, i) => <div key={i} className="mb-3 h-[76px] rounded-xl bg-press-paper-2" />)
            : people.slice(0, 4).map((f, i) => {
                const profile = f.freelancerProfile || {};
                const headline = toPlainText(profile.headline || '') || toPlainText(profile.bio || '');
                const { rating = null, reviewCount = 0 } = f.stats || {};
                // The photo wears a ring in the person's school colour, matching their chip.
                const band = profile.university ? universityBadge(profile.university).primary : '#12161C';
                return (
                  <Link key={f._id} to={`/freelancer/${f._id}`} className={`flex items-center gap-3 py-3 ${i ? 'border-t border-press-border' : 'pt-0'}`}>
                    <span
                      className="relative flex h-[52px] w-[52px] flex-none items-center justify-center overflow-hidden rounded-full font-headline text-[15px] font-extrabold text-press-paper"
                      style={{ background: `linear-gradient(155deg, #12161C 10%, ${band})`, boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${band}` }}
                    >
                      {initials(f)}
                      {f.profilePhoto && (
                        <img
                          src={f.profilePhoto}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          className="absolute inset-0 h-full w-full object-cover"
                        />
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[16px] font-bold">{f.firstName} {f.lastName}</span>
                      {headline && <span className="block truncate text-[14px] text-press-muted">{headline}</span>}
                      {profile.university && <VerifiedSchoolChip university={profile.university} className="mt-1.5 px-2.5" />}
                    </span>
                    <span className="flex-none text-right text-[14px]">
                      <span className="block text-[15px] font-bold">
                        {reviewCount > 0 ? <><span className="text-press-brass-dark" aria-hidden="true">★</span> {rating}</> : 'New'}
                      </span>
                      <span className="block tabular-nums text-press-muted">{f.fromPrice ? `from ${formatUSD(f.fromPrice)}` : 'Quote'}</span>
                    </span>
                  </Link>
                );
              })}
        </section>
      )}

      {/* ── Jobs, Upwork-style ── */}
      <section className="px-4 pt-9" aria-label="Latest jobs">
        <Head title="Latest jobs" to="/job-postings" label="See all" />
        {jobsLoading ? (
          Array.from({ length: 3 }).map((_, i) => <div key={i} className="mb-2 h-[92px] rounded-xl bg-press-paper-2" />)
        ) : jobs.length === 0 ? (
          <p className="text-[15px] text-press-muted">
            No open jobs yet. <Link to="/post-a-job" className="font-semibold text-press-ink underline">Post the first one</Link>
          </p>
        ) : (
          jobs.slice(0, 3).map((job, i) => (
            <Link key={job._id} to={`/job-postings/${job._id}`} className={`block py-4 ${i ? 'border-t border-press-border' : 'pt-1'}`}>
              <span className="block text-[13px] text-press-muted">Posted {timeAgo(job.publishedAt || job.createdAt)}</span>
              <span className="mt-1 block text-[17px] font-semibold leading-snug">{job.title}</span>
              <span className="mt-1 block text-[14px] tabular-nums text-press-muted">
                {[job.budgetType === 'hourly' ? 'Hourly' : job.budgetType === 'negotiable' ? 'Negotiable' : 'Fixed price', formatBudget(job), job.locationType].filter(Boolean).join(' · ')}
              </span>
              {job.category && (
                <span className="mt-2.5 inline-flex h-7 items-center rounded-full bg-press-paper-2 px-2.5 text-[13px] font-semibold">{job.category}</span>
              )}
            </Link>
          ))
        )}
      </section>

      {/* ── One Academy course, on a full-width ink band ── */}
      {course && (
        <section className="mt-10 bg-press-ink px-4 py-8 text-press-paper" aria-label="UniFreelancer Academy">
          <div className="mb-4">
            <h2 className={H2}>UniFreelancer Academy</h2>
            <p className="mt-0.5 text-[14px] text-press-paper/70">Courses and seminars from people who’ve done the work.</p>
          </div>
          <Link to={`/academy/courses/${course._id}`} className="flex items-center gap-3.5 rounded-[20px] bg-press-paper/[0.05] p-3 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)]">
            <span
              className="flex h-[84px] w-[84px] flex-none items-center justify-center rounded-[14px] bg-press-paper/10 text-press-brass-light"
              style={course.thumbnail ? { background: `url(${course.thumbnail}) center/cover no-repeat` } : undefined}
            >
              {!course.thumbnail && <PiGraduationCap size={30} aria-hidden="true" />}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-semibold text-press-brass-light">From the Academy</span>
              <span className="mt-0.5 line-clamp-2 block text-[16px] font-semibold leading-snug">{course.title}</span>
              <span className="mt-0.5 block truncate text-[13px] text-press-paper/70">
                {[course.instructor?.name && `by ${course.instructor.name}`, course.pricing?.amount ? formatUSD(course.pricing.amount) : 'Free'].filter(Boolean).join(' · ')}
              </span>
            </span>
          </Link>
        </section>
      )}

      {/* ── Close ── */}
      <section className="px-4 pb-10 pt-10" aria-label="Start selling">
        <h2 className="text-balance font-headline text-[26px] font-extrabold leading-tight tracking-[-0.02em]">
          {/* brass-dark, not brass: ~3.9:1 on white, fine at this size (same as desktop). */}
          What could you <span className="text-press-brass-dark">earn</span> from what you know?
        </h2>
        <p className="mt-1 text-[15px] text-press-muted">Listing a service is free.</p>
        <div className="mt-5 flex flex-col gap-2.5">
          <Link to="/find-work" className="btn-island btn-island--ink justify-center">
            Post a service
            <span className="btn-island-ic"><PiArrowUpRightLight size={17} aria-hidden="true" /></span>
          </Link>
          <Link to="/post-a-job" className="flex h-[52px] items-center justify-center rounded-full border-[1.5px] border-press-ink text-[15px] font-semibold">
            Post a job
          </Link>
        </div>
      </section>
    </div>
  );
}
